import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "../../lib/utils"
import { FOCUS_RING } from "../../lib/focus-ring"

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    FOCUS_RING
  ),
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 min-h-[44px] max-lg:min-h-[44px] px-3 text-ds-body md:h-8 md:px-2.5 lg:min-h-8",
        sm: "h-10 min-h-[40px] max-lg:min-h-[40px] rounded-md px-2.5 text-ds-caption md:h-7 md:px-2 lg:min-h-7",
        lg: "h-11 min-h-[44px] max-lg:min-h-[44px] rounded-md px-4 text-ds-body md:h-8 md:px-2.5 lg:min-h-8",
        xs: "h-9 min-h-[36px] max-lg:min-h-[36px] rounded-md px-2 text-ds-caption md:h-7 md:px-2 lg:min-h-7 [&_svg]:size-3.5",
        icon: "h-11 w-11 min-h-[44px] min-w-[44px] max-lg:min-h-[44px] max-lg:min-w-[44px] md:h-8 md:w-8 lg:min-h-8 lg:min-w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  "aria-busy": ariaBusy,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button"
  const isDisabled = disabled || loading

  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={asChild ? undefined : isDisabled}
      aria-busy={loading || ariaBusy || undefined}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
          {children}
        </>
      )}
    </Comp>
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
